/**
 * The sidebar tree row's `···` menu (`specs/M1-B10-shell-chrome.md` §4.3).
 *
 * Before B10 a tree row had **no menu at all**, although M0-F4 §3.1 lists "row context menus"
 * as in scope: renaming a board, moving it, changing its type, favouriting it or deleting it
 * all meant opening the board first. monday's menu, measured on a board row
 * (`[c:sidebar-board-menu]`, 240 px wide, 32 px rows, 17 px group gaps):
 *
 *   Open in overlay · Open in new tab
 *   ─
 *   Rename · Move to · Change type · Add to favorites · Duplicate · Save as a template
 *   ─
 *   Delete · Archive            ← `Delete` before `Archive`, as in the group menu
 *
 * `Open in overlay` is monday's board-in-a-modal; this product has no such host yet, so the
 * entry is rendered **disabled with its reason** rather than dropped — a missing first entry
 * changes the menu's shape and the muscle memory with it.
 */
import { useConfirm, useToast } from '@elizmonday/ui';
import { useRef, useState } from 'react';
import { boardCanBeTemplate } from '@elizmonday/shared';
import { Menu, type MenuEntry } from '../board-view/table/menus/Menu';
import { entityUrl } from '../shell/urls';
import { DuplicateBoardDialog } from './DuplicateBoardDialog';
import { MoveBoardDialog } from './MoveBoardDialog';
import { NameDialog } from './NameDialog';
import { SaveAsTemplateDialog } from '../templates';
import { CreateBoardDialog } from './CreateBoardDialog';
import { useFavorite, type FavoriteEntity } from './favorites';
import { BOARD_KIND_LABEL, useBoardMutations, type BoardKind } from './hooks';
import { useFolderMutations, useWorkspaces } from './workspaces';
import { useSidebarData } from '../shell/bootstrap';

export const SIDEBAR_ROW_MENU_WIDTH = 240;

export interface SidebarRowTarget {
  kind: 'board' | 'doc' | 'dashboard' | 'folder';
  id: string;
  name: string;
  /** Boards only. */
  boardKind?: BoardKind;
  workspaceId: string | null;
}

type RowDialog = 'rename' | 'move' | 'duplicate' | 'template' | 'create-board' | null;

/** A folder has no route of its own; every other row type resolves through `entityUrl`. */
function hrefOf(target: SidebarRowTarget): string {
  if (target.kind === 'folder') return '#';
  return entityUrl({ id: target.id, type: target.kind });
}

export function SidebarRowMenu({
  target,
  x,
  y,
  onClose,
  onCollapseAllFolders,
}: {
  target: SidebarRowTarget;
  x: number;
  y: number;
  onClose(): void;
  /** The folder menu's `Collapse all folders`, which is the Content menu's entry verbatim. */
  onCollapseAllFolders?: () => void;
}) {
  const boards = useBoardMutations();
  const folders = useFolderMutations();
  const workspaces = useWorkspaces();
  /** P-279: the folder picker's destinations — the workspace's name and its folder tree. */
  const sidebar = useSidebarData(target.kind === 'folder' ? target.workspaceId : null);
  const favorite = useFavorite(target.kind as FavoriteEntity, target.id);
  const confirm = useConfirm();
  const toast = useToast();
  const [dialog, setDialog] = useState<RowDialog>(null);
  /**
   * `Menu` calls `onClose()` synchronously after a plain entry, in the same handler that asked
   * for the dialog — so a `dialog === null` guard reads the **stale** state and the parent
   * unmounts this component before the dialog ever renders. A ref is the only thing that has
   * already changed by then. `AddMenu` solved the same problem the same way.
   */
  const chose = useRef(false);
  const open = (next: NonNullable<RowDialog>) => () => {
    chose.current = true;
    setDialog(next);
  };

  const removeBoard = async () => {
    const ok = await confirm({
      title: `Delete ${target.name}?`,
      body: 'The board moves to the trash. Anyone who could delete it can restore it for the next 30 days.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    await boards.remove.mutateAsync(target.id);
    toast(`${target.name} moved to the trash`, {
      kind: 'positive',
      action: {
        label: 'Undo',
        onClick: () => void boards.restoreFromTrash.mutateAsync(target.id),
      },
    });
  };

  const removeFolder = async () => {
    const ok = await confirm({
      title: `Delete ${target.name}?`,
      // F4 of B1: a folder never takes its contents with it.
      body: 'The boards inside it move up to the workspace; nothing is deleted with it.',
      confirmLabel: 'Delete folder',
      danger: true,
    });
    if (!ok) return;
    await folders.remove.mutateAsync(target.id);
    toast(`${target.name} deleted`, { kind: 'positive' });
  };

  const archive = async () => {
    await boards.archive.mutateAsync(target.id);
    toast(`${target.name} archived`, {
      kind: 'positive',
      action: { label: 'Undo', onClick: () => void boards.restore.mutateAsync(target.id) },
    });
  };

  if (dialog === 'rename') {
    return (
      <NameDialog
        title={target.kind === 'folder' ? 'Rename folder' : 'Rename board'}
        label="Name"
        initial={target.name}
        confirmLabel="Save"
        testId="sidebar-rename-dialog"
        onCancel={onClose}
        onSubmit={(name) => {
          const done = () => onClose();
          if (target.kind === 'folder') {
            void folders.update.mutateAsync({ id: target.id, name }).then(done);
          } else {
            void boards.update.mutateAsync({ id: target.id, attribute: 'name', value: name }).then(done);
          }
        }}
      />
    );
  }
  if (dialog === 'move') return <MoveBoardDialog boardId={target.id} onClose={onClose} />;
  if (dialog === 'duplicate') return <DuplicateBoardDialog boardId={target.id} onClose={onClose} />;
  if (dialog === 'template') {
    return <SaveAsTemplateDialog boardId={target.id} boardName={target.name} onClose={onClose} />;
  }
  if (dialog === 'create-board') {
    return <CreateBoardDialog workspaceId={target.workspaceId} folderId={target.id} onClose={onClose} />;
  }

  const isBoard = target.kind === 'board';
  const kind = target.boardKind ?? 'public';

  const boardEntries: MenuEntry[] = [
    {
      id: 'overlay',
      label: 'Open in overlay',
      disabled: true,
      title: 'This product opens a board at its own URL; there is no board overlay host yet.',
    },
    {
      id: 'new-tab',
      label: 'Open in new tab',
      onSelect: () => window.open(hrefOf(target), '_blank'),
    },
    { id: 'd1', label: '', divider: true },
    { id: 'rename', label: 'Rename', onSelect: open('rename'), 'data-testid': 'row-rename' },
    { id: 'move', label: 'Move to', onSelect: open('move'), 'data-testid': 'row-move' },
    {
      id: 'type',
      label: 'Change type',
      submenu: (['public', 'share', 'private'] as BoardKind[]).map((next) => ({
        id: `kind-${next}`,
        label: BOARD_KIND_LABEL[next],
        disabled: next === kind,
        onSelect: () => {
          onClose();
          boards.changeKind.mutate({ id: target.id, kind: next });
        },
      })),
    },
    {
      id: 'favorite',
      label: favorite.isFavorite ? 'Remove from favorites' : 'Add to favorites',
      onSelect: () => favorite.toggle(),
      'data-testid': 'row-favorite',
    },
    { id: 'duplicate', label: 'Duplicate', onSelect: open('duplicate') },
    {
      id: 'template',
      label: 'Save as a template',
      disabled: !boardCanBeTemplate(kind),
      title: boardCanBeTemplate(kind) ? undefined : 'Only a main board can become a template.',
      ...(boardCanBeTemplate(kind) ? { onSelect: open('template') } : {}),
    },
    { id: 'd2', label: '', divider: true },
    { id: 'delete', label: 'Delete', danger: true, onSelect: () => void removeBoard() },
    { id: 'archive', label: 'Archive', onSelect: () => void archive() },
  ];

  /**
   * **P-279 · where a folder can go.**
   *
   * The submenu used to list **workspaces only**, which meant a folder that had been dragged
   * inside another folder had no menu path back out: every entry moved it sideways to another
   * workspace, and the one destination it needed — *this* workspace's own top level — was not
   * offered at all. The user hit exactly that on the live Main workspace (`MK` inside `PDT`) and
   * the coordinator had to undo it with a raw `folders.update({ parentFolderId: null })`.
   *
   * monday's picker "lists the workspace itself as a destination (`Main workspace` at the top of
   * the folder picker)" (card P-279), so the shape is: the workspace, then the folders of this
   * workspace, then the other workspaces — the last group being what this menu already did and
   * which nothing else offers. The **current** parent is excluded (moving something where it
   * already is is not a destination), and so are the folder itself and its own descendants,
   * which the service would refuse with `FOLDER_CYCLE` (B1 §5.2) after a pointless round trip.
   */
  const folderRows = sidebar.data?.folders ?? [];
  const currentParentId = folderRows.find((row) => row.id === target.id)?.parentFolderId ?? null;
  const descendantIds = (() => {
    const ids = new Set<string>([target.id]);
    // The tree is shallow and already in memory; a breadth-first sweep is cheaper than a call.
    for (let changed = true; changed; ) {
      changed = false;
      for (const row of folderRows) {
        if (row.parentFolderId !== null && ids.has(row.parentFolderId) && !ids.has(row.id)) {
          ids.add(row.id);
          changed = true;
        }
      }
    }
    return ids;
  })();

  const moveFolderTo = (parentFolderId: string | null) => () => {
    onClose();
    folders.update.mutate({ id: target.id, parentFolderId });
    toast(`${target.name} moved`, { kind: 'positive' });
  };

  const otherWorkspaces = (workspaces.data ?? []).filter(
    (workspace: { id: string }) => workspace.id !== target.workspaceId,
  );

  const folderDestinations: MenuEntry[] = [
    {
      id: 'move-root',
      // monday names the workspace itself; `Workspace` is the fallback before the query lands.
      label: sidebar.data?.workspace.name ?? 'Workspace',
      disabled: currentParentId === null,
      title: currentParentId === null ? 'This folder is already at the workspace level.' : undefined,
      'data-testid': 'row-move-root',
      ...(currentParentId === null ? {} : { onSelect: moveFolderTo(null) }),
    },
    ...folderRows
      .filter((row) => !descendantIds.has(row.id) && row.id !== currentParentId)
      .map((row) => ({
        id: `folder-${row.id}`,
        label: row.name,
        'data-testid': `row-move-folder-${row.id}`,
        onSelect: moveFolderTo(row.id),
      })),
    ...(otherWorkspaces.length > 0
      ? [
          { id: 'move-d1', label: '', divider: true },
          ...otherWorkspaces.map((workspace: { id: string; name: string }) => ({
            id: `ws-${workspace.id}`,
            label: workspace.name,
            onSelect: () => {
              onClose();
              folders.update.mutate({ id: target.id, workspaceId: workspace.id });
            },
          })),
        ]
      : []),
  ];

  /**
   * A folder is not a board: no type, no duplicate, no template, no archive. monday's own
   * folder menu (`[c:sandbox-folder-menu]`, 240 px, 6 rows) is
   * `Rename · Change color | Move to · Create in folder | Collapse all folders · Delete` —
   * and it carries **no** favourite entry.
   */
  const folderEntries: MenuEntry[] = [
    { id: 'rename', label: 'Rename', onSelect: open('rename'), 'data-testid': 'row-rename' },
    {
      id: 'colour',
      label: 'Change color',
      submenu: FOLDER_COLORS.map((color) => ({
        id: `colour-${color}`,
        label: color,
        onSelect: () => {
          onClose();
          folders.update.mutate({ id: target.id, color });
        },
      })),
    },
    { id: 'd1', label: '', divider: true },
    {
      id: 'move',
      label: 'Move to',
      'data-testid': 'row-move',
      submenu: folderDestinations,
    },
    {
      id: 'create-in',
      label: 'Create in folder',
      submenu: [{ id: 'create-board', label: 'Board', onSelect: open('create-board') }],
    },
    { id: 'd2', label: '', divider: true },
    {
      id: 'collapse-all',
      label: 'Collapse all folders',
      disabled: onCollapseAllFolders === undefined,
      onSelect: onCollapseAllFolders,
    },
    { id: 'delete', label: 'Delete', danger: true, onSelect: () => void removeFolder() },
  ];

  /** A doc / dashboard row: rename and favourite are the only writes the shell owns. */
  const otherEntries: MenuEntry[] = [
    {
      id: 'new-tab',
      label: 'Open in new tab',
      onSelect: () => window.open(hrefOf(target), '_blank'),
    },
    { id: 'd1', label: '', divider: true },
    {
      id: 'favorite',
      label: favorite.isFavorite ? 'Remove from favorites' : 'Add to favorites',
      onSelect: () => favorite.toggle(),
      'data-testid': 'row-favorite',
    },
    {
      id: 'copy-link',
      label: 'Copy link',
      onSelect: () =>
        void navigator.clipboard
          ?.writeText(`${window.location.origin}${hrefOf(target)}`)
          .then(() => toast('Link copied', { kind: 'positive' }))
          .catch(() => toast('The link could not be copied', { kind: 'negative' })),
    },
  ];

  const entries =
    target.kind === 'folder' ? folderEntries : isBoard ? boardEntries : otherEntries;

  return (
    <Menu
      entries={entries}
      x={x}
      y={y}
      width={SIDEBAR_ROW_MENU_WIDTH}
      label={`${target.name} options`}
      onClose={() => {
        if (!chose.current) onClose();
      }}
    />
  );
}

/** `FolderColor` as the API spells it (B1 §5.2); applied verbatim by `SidebarRow`. */
const FOLDER_COLORS = [
  'done-green',
  'working_orange',
  'dark-red',
  'sunset',
  'bright-blue',
  'purple',
  'dark_purple',
  'dark-blue',
  'lipstick',
  'sofia_pink',
  'egg_yolk',
  'explosive',
] as const;
